import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './crm-ui.scss';

function CrmPopover({
  open,
  onClose,
  anchorRef,
  children,
  align = 'end',
  role = 'menu',
  className = '',
  offset = 8,
  autoFocus = true,
}) {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, visibility: 'hidden' });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const viewportPadding = 8;
    let left = align === 'start'
      ? anchorRect.left
      : anchorRect.right - popoverRect.width;
    let top = anchorRect.bottom + offset;

    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - popoverRect.width - viewportPadding));
    if (top + popoverRect.height > window.innerHeight - viewportPadding) {
      top = Math.max(viewportPadding, anchorRect.top - popoverRect.height - offset);
    }

    setPosition({ top, left, visibility: 'visible' });
  }, [align, anchorRef, offset]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition, children]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (popoverRef.current?.contains(event.target) || anchorRef?.current?.contains(event.target)) return;
      onClose?.();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        anchorRef?.current?.focus?.();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    if (autoFocus) {
      requestAnimationFrame(() => {
        popoverRef.current?.querySelector(
          '[role="menuitem"]:not([disabled]), button:not([disabled]), [href], input:not([disabled])'
        )?.focus();
      });
    }

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef, autoFocus, onClose, open, updatePosition]);

  if (!open) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className={`crm-popover ${className}`.trim()}
      role={role}
      style={position}
    >
      {children}
    </div>,
    document.body
  );
}

export default CrmPopover;
